import { DecimalPipe, getCurrencySymbol } from '@angular/common';
import { Pipe, PipeTransform, inject } from '@angular/core';
import { UnitsConversionHelper } from '@cfp/core';
import { MeasurementService } from '@cfp/shared/ui/measurement';
import { CfpCurrencyPipe } from '@cfp/ui';
import {
  BuildingChangeLogItemDTO,
  BuildingDetailsDTO,
  BuildingSystemChangeLogItemDTO,
  ParameterUnitTypeDTO
} from 'csr-asset-app';
import { Observable, map, of } from 'rxjs';
import { AuditTrailTranslateService } from '../../services';

const unitMap: Map<string, string> = new Map<string, string>([
  ['CHARACTERISTICS:surface', 'measurement'],

  ['ENERGY_COST:building.energylabel.electricityprice', 'currency/electricityUnit'],
  ['ENERGY_COST:building.energylabel.gasprice', 'currency/gasUnit'],
  ['ENERGY_COST:building.energylabel.heatprice', 'currency/heatingUnit'],
  ['ENERGY_COST:building.energylabel.oilprice', 'currency/oilUnit'],
  ['ENERGY_COST:building.energylabel.coalprice', 'currency/coalUnit'],
  ['ENERGY_COST:building.energylabel.pelletprice', 'currency/pelletUnit'],
  ['ENERGY_COST:building.energylabel.woodprice', 'currency/woodUnit'],

  ['ENERGY_CONSUMPTION:building.energylabel.gasusage', 'gasUnit'],
  ['ENERGY_CONSUMPTION:building.energylabel.electricity', 'electricityUnit'],
  ['ENERGY_CONSUMPTION:building.energylabel.returnElectricity', 'electricityUnit'],
  ['ENERGY_CONSUMPTION:building.energylabel.heatusage', 'heatingUnit'],
  ['ENERGY_CONSUMPTION:building.energylabel.oilusage', 'oilUnit'],
  ['ENERGY_CONSUMPTION:building.energylabel.coalusage', 'coalUnit'],
  ['ENERGY_CONSUMPTION:building.energylabel.pelletusage', 'pelletUnit'],
  ['ENERGY_CONSUMPTION:building.energylabel.woodusage', 'woodUnit']
]);

@Pipe({
  name: 'auditTrailUnit'
})
export class AuditTrailUnitPipe implements PipeTransform {
  private readonly measurementService = inject(MeasurementService);
  private readonly unitsConversionHelper = inject(UnitsConversionHelper);
  private readonly auditTrailTranslateService = inject(AuditTrailTranslateService);
  private readonly decimalPipe = inject(DecimalPipe);
  private readonly cfpCurrencyPipe = inject(CfpCurrencyPipe);

  public transform(
    value: string,
    logItem: BuildingChangeLogItemDTO | BuildingSystemChangeLogItemDTO,
    building: BuildingDetailsDTO
  ): Observable<string> {
    if (value === '-' || !logItem?.category || !logItem?.property) {
      return of(value);
    }

    const unitKey: string = `${logItem.category}:${logItem.property}`;
    const unit: string = logItem.unitType ?? unitMap.get(unitKey);

    return this.formatValue(value, unit, building, logItem).pipe(
      map((formattedValue) => {
        return `${formattedValue} ${this.getUnit(unit, logItem.currencyCode)}`.trim();
      })
    );
  }

  private formatValue(
    value: string,
    unit: string,
    building: BuildingDetailsDTO,
    logItem: BuildingChangeLogItemDTO | BuildingSystemChangeLogItemDTO
  ): Observable<string> {
    // electricity
    if (unit === 'electricityUnit' && this.isNumeric(value)) {
      return of(this.decimalPipe.transform(value, '1.0-0'));
    }
    if (unit === 'currency/electricityUnit' && this.isNumeric(value)) {
      return of(
        this.decimalPipe.transform(this.cfpCurrencyPipe.transform(Number(value), logItem.currencyCode), '1.0-0')
      );
    }
    // gas
    if (unit === 'gasUnit' && this.isNumeric(value)) {
      return this.unitsConversionHelper
        .m3ToTargetGasUnitsAsync(Number(value), building.address.countryCode, building.address.regionCode)
        .pipe(map((it) => this.decimalPipe.transform(it, '1.0-2')));
    }
    if (unit === 'currency/gasUnit' && this.isNumeric(value)) {
      return this.unitsConversionHelper
        .gasUnitCostM3ToTargetAsync(Number(value), building.address.countryCode, building.address.regionCode)
        .pipe(map((it) => this.decimalPipe.transform(it, '1.0-4')));
    }
    // oil
    if (unit === 'oilUnit' && this.isNumeric(value)) {
      return this.unitsConversionHelper
        .literToTargetOilUnitsAsync(Number(value), building.address.countryCode, building.address.regionCode)
        .pipe(map((it) => this.decimalPipe.transform(it, '1.0-0')));
    }
    if (unit === 'currency/oilUnit' && this.isNumeric(value)) {
      return this.unitsConversionHelper
        .oilUnitCostLiterToTargetAsync(Number(value), building.address.countryCode, building.address.regionCode)
        .pipe(map((it) => this.decimalPipe.transform(it, '1.0-4')));
    }
    // coal
    if (unit === 'coalUnit' && this.isNumeric(value)) {
      return this.unitsConversionHelper
        .tonToTargetCoalUnitsAsync(Number(value), building.address.countryCode, building.address.regionCode)
        .pipe(map((it) => this.decimalPipe.transform(it, '1.0-0')));
    }
    if (unit === 'currency/oilUnit' && this.isNumeric(value)) {
      return this.unitsConversionHelper
        .coalUnitCostTonsToTargetAsync(Number(value), building.address.countryCode, building.address.regionCode)
        .pipe(map((it) => this.decimalPipe.transform(it, '1.0-4')));
    }
    // pellet
    if (unit === 'pelletUnit' && this.isNumeric(value)) {
      return this.unitsConversionHelper
        .tonToTargetPelletUnitsAsync(Number(value), building.address.countryCode, building.address.regionCode)
        .pipe(map((it) => this.decimalPipe.transform(it, '1.0-0')));
    }
    if (unit === 'currency/pelletUnit' && this.isNumeric(value)) {
      return this.unitsConversionHelper
        .pelletUnitCostTonsToTargetAsync(Number(value), building.address.countryCode, building.address.regionCode)
        .pipe(map((it) => this.decimalPipe.transform(it, '1.0-4')));
    }
    // wood
    if (unit === 'woodUnit' && this.isNumeric(value)) {
      return this.unitsConversionHelper
        .m3ToTargetWoodUnitsAsync(Number(value), building.address.countryCode, building.address.regionCode)
        .pipe(map((it) => this.decimalPipe.transform(it, '1.0-0')));
    }
    if (unit === 'currency/woodUnit' && this.isNumeric(value)) {
      return this.unitsConversionHelper
        .woodUnitCostM3ToTargetAsync(Number(value), building.address.countryCode, building.address.regionCode)
        .pipe(map((it) => this.decimalPipe.transform(it, '1.0-4')));
    }

    // measure parameters
    if (
      unit != ParameterUnitTypeDTO.NONE &&
      Object.values(ParameterUnitTypeDTO).some((parameterUnitType) => parameterUnitType === unit)
    ) {
      return of(
        this.measurementService
          .convertParameter({
            unitType: unit as ParameterUnitTypeDTO,
            value: value,
            allowedDecimals: 2
          })
          .toString()
      );
    }

    // no numeric value, so maybe an enum value that needs translation
    return of(this.auditTrailTranslateService.findTranslation(value));
  }

  private isNumeric(text: string): boolean {
    return /\D/.test(text);
  }

  private getUnit(unit: string, currency: string): string {
    if (!unit) return '';

    let result: string = unit;

    if (result.includes('currency')) {
      result = this.currencyReplaceHelper(result, currency);
    }
    if (result.includes('measurement')) {
      result = this.measurementHelper(result);
    }
    if (result.includes('gasUnit')) {
      // GasUnits: example - m3
      result = this.energyUnitHelper(result);
    }
    if (result.includes('electricityUnit')) {
      // Electricity units: example - kWh
      result = this.energyUnitHelper(result);
    }
    if (result.includes('heatingUnit')) {
      result = this.energyUnitHelper(result);
    }
    if (result.includes('oilUnit')) {
      // OilUnits : example - l
      result = this.energyUnitHelper(result);
    }

    if (result.includes('coalUnit')) {
      result = this.energyUnitHelper(result);
    }

    if (result.includes('woodUnit')) {
      result = this.energyUnitHelper(result);
    }

    if (result.includes('pelletUnit')) {
      result = this.energyUnitHelper(result);
    }

    // measure parameters. This is already displayed in the parameter-label.
    if (Object.values(ParameterUnitTypeDTO).some((parameterUnitType) => parameterUnitType === unit)) {
      result = '';
    }

    return result;
  }

  private currencyReplaceHelper(unit: string, currency: string): string {
    if (unit && unit.includes('currency') && currency) {
      const currencySymbol: string = getCurrencySymbol(currency, 'narrow');
      return unit.replace('currency', currencySymbol);
    }
    return unit;
  }

  private measurementHelper(unit: string): string {
    if (unit && unit.includes('measurement')) {
      return unit.replace('measurement', this.measurementService.getSquareUnit());
    }

    return unit;
  }

  private energyUnitHelper(unit: string): string {
    if (unit && unit.includes('gasUnit')) {
      unit = unit.replace('gasUnit', this.unitsConversionHelper.getGasUnitsHtml());
    }

    if (unit && unit.includes('electricityUnit')) {
      unit = unit.replace('electricityUnit', 'kWh');
    }

    if (unit && unit.includes('oilUnit')) {
      unit = unit.replace('oilUnit', this.unitsConversionHelper.getOilUnits());
    }

    if (unit && unit.includes('heatingUnit')) {
      return unit.replace('heatingUnit', this.unitsConversionHelper.getHeatUnits());
    }

    if (unit && unit.includes('coalUnit')) {
      return unit.replace('coalUnit', this.unitsConversionHelper.getCoalUnits());
    }

    if (unit && unit.includes('pelletUnit')) {
      return unit.replace('pelletUnit', this.unitsConversionHelper.getPelletUnits());
    }

    if (unit && unit.includes('woodUnit')) {
      return unit.replace('woodUnit', this.unitsConversionHelper.getWoodUnitsHtml());
    }

    return unit;
  }
}
